import { query, getDocs, orderBy, limit, startAfter, where, collection } from 'firebase/firestore';
import { feedbackCollection, db } from '../firebase';

const FILTER_QUERY_LIMIT = 500;

// All public fetch functions default to excluding silent_duplicate fraud-trap
// rows.  Pass `includeSilentDuplicates: true` only from the admin Silent
// Duplicates page where they should be visible.
const stripSilent = (rows, includeSilentDuplicates) =>
  includeSilentDuplicates ? rows : rows.filter(r => r.silent_duplicate !== true);

// Fetch all supervisors from the supervisors collection
export async function fetchAllSupervisors() {
  try {
    // Create a reference to the supervisors collection
    const supervisorsCollection = collection(db, "supervisors");
    
    // Create a query to get all supervisors, ordered by display_name
    const supervisorsQuery = query(
      supervisorsCollection,
      orderBy('display_name', 'asc')
    );
    
    // Execute the query
    const snapshot = await getDocs(supervisorsQuery);
    
    // Map the results to an array of supervisor objects
    const supervisors = snapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data(),
      // Create a label for the dropdown combining display_name and ixora_user_id
      label: `${doc.data().display_name || 'Unknown'} (${doc.data().ixora_user_id || 'No ID'})`
    }));
    
    console.log(`Fetched ${supervisors.length} supervisors`);
    return supervisors;
  } catch (error) {
    console.error("Error fetching supervisors:", error);
    return [];
  }
}

// Fetch all feedbacks for a given areaNum from Firestore
export async function fetchFeedbacksByAreaNum(areaNum, { includeSilentDuplicates = false } = {}) {
  const q = query(feedbackCollection, where('shop_area', '==', String(areaNum)));
  const snapshot = await getDocs(q);
  return stripSilent(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })), includeSilentDuplicates);
}

// Fetch all feedbacks with multiple filter criteria.
//
// `supervisorIds` (preferred): an array of all ixora_user_id values a supervisor
// has used, including legacy ones from migrate-legacy-supervisors.mjs. Use this
// over `supervisorId` so historical feedback (stamped with legacy ids) still
// appears post-migration.
//
// `supervisorId` (legacy single-id): kept for backward compatibility.
export async function fetchAllFeedbacksWithFilters({
  areaNum = null,
  supervisorId = null,
  supervisorIds = null,
  supervisorName = null,
  shopName = null,
  phoneNumber = null,
  includeSilentDuplicates = false,
}) {
  // Normalise the supervisor id input — caller may pass either form.
  const effectiveSupIds = Array.isArray(supervisorIds) && supervisorIds.length > 0
    ? supervisorIds.filter((v) => typeof v === 'string' && v.length > 0).slice(0, 30)
    : (supervisorId ? [supervisorId] : []);

  console.log(
    "fetchAllFeedbacksWithFilters called with areaNum:",
    areaNum,
    "supervisorIds:",
    effectiveSupIds,
  );

  try {
    // Build query based on filters
    let queryConstraints = [];
    let hasConstraints = false;

    if (areaNum) {
      hasConstraints = true;
    }

    // Add supervisor filter if specified (matched against
    // feedback_collector_ixora_userID; uses `in` when more than one id, == otherwise).
    if (effectiveSupIds.length === 1) {
      queryConstraints.push(
        where('feedback_collector_ixora_userID', '==', effectiveSupIds[0]),
      );
      hasConstraints = true;
    } else if (effectiveSupIds.length > 1) {
      queryConstraints.push(
        where('feedback_collector_ixora_userID', 'in', effectiveSupIds),
      );
      hasConstraints = true;
    }
    
    // If we have no constraints yet, we might need to fetch all documents
    // But first, try to filter by area if that's provided
    if (areaNum) {
      return await fetchFeedbacksByArea(areaNum, queryConstraints, includeSilentDuplicates);
    }
    
    // If we have supervisor constraints but no area constraints
    if (hasConstraints) {
      // Create the query with the constraints
      const baseQuery = query(
        feedbackCollection,
        ...queryConstraints,
        orderBy('feedback_collection_DateTime', 'desc'),
        limit(FILTER_QUERY_LIMIT)
      );

      const snapshot = await getDocs(baseQuery);
      let results = stripSilent(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })), includeSilentDuplicates);
      console.log(`Found ${results.length} documents with supervisor filter`);

      return results;
    }

    // If no constraints, fetch all documents and apply client-side filtering
    const allDocsQuery = query(
      feedbackCollection,
      orderBy('feedback_collection_DateTime', 'desc')
    );

    const allDocsSnapshot = await getDocs(allDocsQuery);
    let results = stripSilent(allDocsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })), includeSilentDuplicates);
    console.log(`Fetched ${results.length} total documents`);
    
    // Apply additional filters in memory if needed
    if (supervisorName && supervisorName.trim() !== '') {
      const searchTerm = supervisorName.toLowerCase().trim();
      results = results.filter(doc => 
        doc.feedback_collector_name && 
        doc.feedback_collector_name.toLowerCase().includes(searchTerm)
      );
    }
    
    if (shopName && shopName.trim() !== '') {
      const searchTerm = shopName.toLowerCase().trim();
      results = results.filter(doc => 
        doc.shop_name && 
        doc.shop_name.toLowerCase().includes(searchTerm)
      );
    }
    
    if (phoneNumber && phoneNumber.trim() !== '') {
      const searchTerm = phoneNumber.trim();
      results = results.filter(doc => 
        doc.phone_number && 
        String(doc.phone_number).includes(searchTerm)
      );
    }
    
    return results;
  } catch (error) {
    console.error("Error in fetchAllFeedbacksWithFilters:", error);
    return [];
  }
}

// Helper function to fetch feedbacks by area with various approaches
async function fetchFeedbacksByArea(areaNum, additionalConstraints = [], includeSilentDuplicates = false) {
  // Try 4 different approaches to filter by area
  let allResults = [];
  
  // Approach 1: shop_area as string
  try {
    const stringQuery = query(
      feedbackCollection, 
      where('shop_area', '==', String(areaNum)),
      ...additionalConstraints,
      orderBy('feedback_collection_DateTime', 'desc'),
      limit(FILTER_QUERY_LIMIT)
    );
    console.log("Executing query with shop_area as string:", String(areaNum));
    const stringSnapshot = await getDocs(stringQuery);
    const stringResults = stringSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log("Results with shop_area as string:", stringResults.length);
    allResults = [...allResults, ...stringResults];
  } catch (e) {
    console.error("Error querying shop_area as string:", e);
  }
  
  // Approach 2: shop_area as number
  try {
    const numQuery = query(
      feedbackCollection, 
      where('shop_area', '==', areaNum),
      ...additionalConstraints,
      orderBy('feedback_collection_DateTime', 'desc'),
      limit(FILTER_QUERY_LIMIT)
    );
    console.log("Executing query with shop_area as number:", areaNum);
    const numSnapshot = await getDocs(numQuery);
    const numResults = numSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log("Results with shop_area as number:", numResults.length);
    
    // Add only non-duplicate results
    const existingIds = new Set(allResults.map(r => r.id));
    numResults.forEach(doc => {
      if (!existingIds.has(doc.id)) {
        allResults.push(doc);
      }
    });
  } catch (e) {
    console.error("Error querying shop_area as number:", e);
  }
  
  // Approach 3: area as string
  try {
    const areaStringQuery = query(
      feedbackCollection, 
      where('area', '==', String(areaNum)),
      ...additionalConstraints,
      orderBy('feedback_collection_DateTime', 'desc'),
      limit(FILTER_QUERY_LIMIT)
    );
    console.log("Executing query with area as string:", String(areaNum));
    const areaStringSnapshot = await getDocs(areaStringQuery);
    const areaStringResults = areaStringSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log("Results with area as string:", areaStringResults.length);
    
    // Add only non-duplicate results
    const existingIds = new Set(allResults.map(r => r.id));
    areaStringResults.forEach(doc => {
      if (!existingIds.has(doc.id)) {
        allResults.push(doc);
      }
    });
  } catch (e) {
    console.error("Error querying area as string:", e);
  }
  
  // Approach 4: area as number
  try {
    const areaNumQuery = query(
      feedbackCollection, 
      where('area', '==', areaNum),
      ...additionalConstraints,
      orderBy('feedback_collection_DateTime', 'desc'),
      limit(FILTER_QUERY_LIMIT)
    );
    console.log("Executing query with area as number:", areaNum);
    const areaNumSnapshot = await getDocs(areaNumQuery);
    const areaNumResults = areaNumSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log("Results with area as number:", areaNumResults.length);
    
    // Add only non-duplicate results
    const existingIds = new Set(allResults.map(r => r.id));
    areaNumResults.forEach(doc => {
      if (!existingIds.has(doc.id)) {
        allResults.push(doc);
      }
    });
  } catch (e) {
    console.error("Error querying area as number:", e);
  }
  
  console.log("Total combined results:", allResults.length);
  // Strip silent_duplicate fraud-trap rows from area searches (unless caller opts in).
  allResults = stripSilent(allResults, includeSilentDuplicates);
  
  // If we still have no results, fetch all documents and filter in memory
  if (allResults.length === 0 && additionalConstraints.length === 0) {
    console.log("No results from index queries, falling back to client-side filtering");
    try {
      const allDocsQuery = query(
        feedbackCollection,
        orderBy('feedback_collection_DateTime', 'desc'),
        limit(FILTER_QUERY_LIMIT)
      );
      const allDocsSnapshot = await getDocs(allDocsQuery);
      const allDocs = allDocsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Filter by area in memory
      allResults = allDocs.filter(doc => {
        const docShopArea = doc.shop_area || doc.area;
        if (!docShopArea) return false;
        
        if (typeof docShopArea === 'string') {
          return docShopArea === String(areaNum);
        } else if (typeof docShopArea === 'number') {
          return docShopArea === areaNum;
        }
        return false;
      });
      
      console.log("Results after client-side filtering:", allResults.length);
    } catch (e) {
      console.error("Error in client-side filtering:", e);
    }
  }
  
  return allResults;
}

// Fetch a page of feedbacks from Firestore
//
// Pagination note: stripSilent runs after the page is fetched, so a page can
// return fewer than pageSize rows if it contained silent_duplicate docs.  This
// is acceptable for the admin list (preserves stable cursor); callers that need
// exact pageSize should over-fetch or pass includeSilentDuplicates=true.
export async function fetchFeedbackPage({ pageSize = 5, lastVisible = null, includeSilentDuplicates = false }) {
  let q = query(
    feedbackCollection,
    orderBy('feedback_collection_DateTime', 'desc'),
    limit(pageSize)
  );
  if (lastVisible) {
    q = query(
      feedbackCollection,
      orderBy('feedback_collection_DateTime', 'desc'),
      startAfter(lastVisible),
      limit(pageSize)
    );
  }
  const snapshot = await getDocs(q);
  const feedbacks = stripSilent(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })), includeSilentDuplicates);
  const newLastVisible = snapshot.docs[snapshot.docs.length - 1];
  return { feedbacks, lastVisible: newLastVisible };
}
